<template>
    <div>
        <FormGroup :label="__('admin::labels.starts_at')" :error="form.errors.starts_at">
            <Datepicker name="starts_at" :has-error="exists(form.errors.starts_at)" v-model="form.starts_at" :required="true" />
        </FormGroup>

        <FormGroup :label="__('admin::labels.ends_at')" :error="form.errors.ends_at">
            <Datepicker name="ends_at" :has-error="exists(form.errors.ends_at)" v-model="form.ends_at" :required="true" :minDate="form.starts_at" />
        </FormGroup>

        <FormGroup :label="__('admin::labels.returns_at')" :error="form.errors.returns_at">
            <Datepicker name="returns_at" :has-error="exists(form.errors.returns_at)" v-model="form.returns_at" :required="false" :minDate="form.ends_at" />
        </FormGroup>

        <FormGroup :label="__('admin::labels.days_to_show_notice_before_start')" :error="form.errors.days_to_show_notice_before_start">
            <Input name="days_to_show_notice_before_start" type="number" step="1" :has-error="exists(form.errors.days_to_show_notice_before_start)" v-model="form.days_to_show_notice_before_start" :required="true" />
        </FormGroup>

        <FormGroup :label="__('admin::labels.deputy_user')" :error="form.errors.deputy_user_id">
            <Select name="deputy_user_id" :options="userOptions" :has-error="exists(form.errors.deputy_user_id)" v-model="form.deputy_user_id" :allow-empty="true" />
        </FormGroup>

        <FormGroup :label="__('admin::labels.comment')" :error="form.errors.comment">
            <Textarea name="comment" :has-error="exists(form.errors.comment)" v-model="form.comment" />
        </FormGroup>

        <Heading type="h2" class="mt-4">{{ __('admin::labels.texts') }}</Heading>

        <FormGroup>
            <Toggle v-model:checked="form.use_default_messages" :label="__('admin::labels.use_default_messages')" />
        </FormGroup>

        <div v-if="form.use_default_messages">
            <FormGroup :label="__('admin::labels.appendix_before_vacation_message')">
                <div class="w-full bg-gray-100 px-4 py-3 rounded-md shadow-sm">
                    <VueMarkdown :source="vacationMessageDefault.appendix_before_vacation_message" />
                </div>
            </FormGroup>

            <FormGroup :label="__('admin::labels.appendix_during_vacation_message')">
                <div class="w-full bg-gray-100 px-4 py-3 rounded-md shadow-sm">
                    <VueMarkdown :source="vacationMessageDefault.appendix_during_vacation_message" />
                </div>
            </FormGroup>

            <FormGroup :label="__('admin::labels.autoreply_subject')">
                <div class="w-full bg-gray-100 px-4 py-3 rounded-md shadow-sm">
                    <VueMarkdown :source="vacationMessageDefault.autoreply_subject" />
                </div>
            </FormGroup>

            <FormGroup :label="__('admin::labels.autoreply_message')">
                <div class="w-full bg-gray-100 px-4 py-3 rounded-md shadow-sm">
                    <VueMarkdown :source="vacationMessageDefault.autoreply_message" />
                </div>
            </FormGroup>
        </div>

        <div v-else>
            <FormGroup :label="__('admin::labels.appendix_before_vacation_message')" :error="form.errors.appendix_before_vacation_message">
                <MarkdownEditor name="appendix_before_vacation_message" v-model="form.appendix_before_vacation_message" />
            </FormGroup>

            <FormGroup :label="__('admin::labels.appendix_during_vacation_message')" :error="form.errors.appendix_during_vacation_message">
                <MarkdownEditor name="appendix_during_vacation_message" v-model="form.appendix_during_vacation_message" />
            </FormGroup>

            <FormGroup :label="__('admin::labels.autoreply_subject')" :error="form.errors.autoreply_subject">
                <Input name="autoreply_subject" type="text" :has-error="exists(form.errors.autoreply_subject)" v-model="form.autoreply_subject" :required="false" />
            </FormGroup>

            <FormGroup :label="__('admin::labels.autoreply_message')" :error="form.errors.autoreply_message">
                <MarkdownEditor name="autoreply_message" v-model="form.autoreply_message" />
            </FormGroup>
        </div>

        <div v-if="managerMode" class="mb-2">
            <Heading type="h2" class="mt-4">{{ __('admin::labels.approval') }}</Heading>

            <FormGroup class="space-y-2">
                <Toggle v-model:checked="form.is_approved" :label="__('admin::labels.is_approved')" />
                <Toggle v-model:checked="form.synced_with_salary" :label="__('admin::labels.synced_with_salary')" />
            </FormGroup>
        </div>

    </div>
</template>


<script>
import VueMarkdown from 'vue-markdown-render'
import Textarea from "./Textarea.vue";

export default {
    components: {
        Textarea,
        VueMarkdown,
    },
    props: {
        modelValue: Object,
        userOptions: Object,
        vacationMessageDefault: Object,
        managerMode: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            form: this.modelValue,
        }
    },
    watch: {
        'form.ends_at': function (newVal) {
            this.setDaysToShowNotice();
        },
        'form.starts_at': function () {
            this.setDaysToShowNotice();
        },
        'form.use_default_messages': function (newVal) {
            if (newVal) {
                this.form.appendix_before_vacation_message = this.form.appendix_before_vacation_message || this.vacationMessageDefault?.appendix_before_vacation_message;
                this.form.appendix_during_vacation_message = this.form.appendix_during_vacation_message || this.vacationMessageDefault?.appendix_during_vacation_message;
                this.form.autoreply_subject = this.form.autoreply_subject || this.vacationMessageDefault?.autoreply_subject;
                this.form.autoreply_message = this.form.autoreply_message || this.vacationMessageDefault?.autoreply_message;
            }
        },
        form: {
            handler: function (newValue) {
                this.$emit('update:modelValue', newValue);
            },
            deep: true,
        },
    },
    methods: {
        setDaysToShowNotice() {
            if (this.form.starts_at && this.form.ends_at && !this.form.days_to_show_notice_before_start) {
                const vacationDays = Math.ceil(Math.abs(new Date(this.form.ends_at) - new Date(this.form.starts_at)) / (1000 * 60 * 60 * 24)) + 1;

                if (vacationDays >= 7) {
                    this.form.days_to_show_notice_before_start = 14;
                } else if (vacationDays >= 3) {
                    this.form.days_to_show_notice_before_start = 7;
                } else {
                    this.form.days_to_show_notice_before_start = 1;
                }
            }
        },
    },
}
</script>
